import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PatientQuickNotesRepositoryService, QuickNote } from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientQuickNotesStore — single quick note attached to the current patient.
 *
 * The quick note arrives as part of the Patient payload from the server, so
 * there is no separate fetch on patient change. This store seeds its `note`
 * signal from `PatientStore.current()?.quickNote` and exposes `update()` for
 * persisting edits via the repository.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientQuickNotesStore {
  private patientStore = inject(PatientStore);
  private repo = inject(PatientQuickNotesRepositoryService);

  private readonly _note = signal<QuickNote | null>(null);
  private readonly _saving = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly note = this._note.asReadonly();
  readonly saving = this._saving.asReadonly();
  readonly error = this._error.asReadonly();
  readonly hasNote = computed(() => {
    const note = this._note();
    return !!note && !!note.note && note.note.length > 0;
  });

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      this._note.set(patient?.quickNote ?? null);
      this._error.set(null);
    });
  }

  /**
   * Persist a new value for the current patient's quick note. Optimistic —
   * updates the local signal immediately and rolls back on error.
   */
  update(note: QuickNote): Observable<boolean> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientQuickNotesStore.update() called with no current patient');
    }
    const previous = this._note();
    this._note.set(note);
    this._saving.set(true);
    this._error.set(null);
    return this.repo.update(patient.id, note).pipe(
      tap({
        next: () => this._saving.set(false),
        error: error => {
          this._note.set(previous);
          this._saving.set(false);
          this._error.set('Could not update quick note');
          
        }
      })
    );
  }
}
